<template>
    <div class="history-diff-panel">
        <div class="history-toolbar">
            <label class="history-toolbar-label">Compare</label>
            <multiselect
                class="history-select"
                :value="fromVersion"
                :options="newestFirst"
                :custom-label="optionLabel"
                track-by="number"
                :show-labels="false"
                :allow-empty="false"
                :searchable="false"
                :disabled="loading"
                @input="$emit('update:from', $event.number)"
            />
            <i class="far fa-arrow-right history-arrow"></i>
            <multiselect
                class="history-select"
                :value="toVersion"
                :options="newestFirst"
                :custom-label="optionLabel"
                track-by="number"
                :show-labels="false"
                :allow-empty="false"
                :searchable="false"
                :disabled="loading"
                @input="$emit('update:to', $event.number)"
            />

            <div v-if="effectiveMode != 'clean'" class="form-check history-collapse">
                <input :id="`history-collapse-${_uid}`" class="form-check-input" type="checkbox" :checked="collapse" @change="$emit('update:collapse', $event.target.checked)" />
                <label class="form-check-label" :for="`history-collapse-${_uid}`">Collapse unchanged</label>
            </div>

            <div class="btn-group history-modes" role="group" aria-label="Diff view mode">
                <template v-for="option in modes">
                    <input
                        :key="`input-${option.value}`"
                        :id="`history-mode-${option.value}`"
                        type="radio"
                        class="btn-check"
                        :name="`history-mode`"
                        autocomplete="off"
                        :checked="effectiveMode == option.value"
                        :disabled="single"
                        @change="$emit('update:mode', option.value)"
                    />
                    <label :key="`label-${option.value}`" class="btn btn-outline-primary btn-sm" :for="`history-mode-${option.value}`">{{ option.label }}</label>
                </template>
            </div>
        </div>

        <div class="history-diff">
            <template v-if="loading">
                <div class="skeleton-line w-50 mb-3"></div>
                <div v-for="index in 3" :key="index" class="history-field skeleton-field">
                    <div class="skeleton-line w-25"></div>
                    <div class="skeleton-line w-100"></div>
                    <div class="skeleton-line w-75"></div>
                </div>
            </template>

            <template v-else-if="fromVersion && toVersion">
                <div class="history-summary">
                    <span><b>v{{ fromVersion.number }}</b> <i class="far fa-arrow-right history-arrow"></i> <b>v{{ toVersion.number }}</b></span>
                    <span>{{ changedFields }} of {{ fields.length }} fields changed</span>
                    <span v-if="formattingFields">{{ formattingFields }} with formatting changes only</span>
                    <span class="added">+{{ totals.added }}</span>
                    <span class="removed">−{{ totals.removed }}</span>
                </div>

                <field-diff
                    v-for="field in fields"
                    :key="field.key"
                    :field="field"
                    :mode="effectiveMode"
                    :collapse="collapse"
                    :from-label="`v${fromVersion.number}${fromVersion.current ? ' (current)' : ''}`"
                    :to-label="`v${toVersion.number}${toVersion.current ? ' (current)' : ''}`"
                />
            </template>
        </div>

        <slot name="footer"></slot>
    </div>
</template>

<script>
    import moment from 'moment'
    import FieldDiff from './FieldDiff.vue'
    import { diffStates } from 'utils/story-history'

    export default {
        components: {
            FieldDiff
        },

        props: {
            versions: { type: Array, default: () => [] },
            from: Number,
            to: Number,
            mode: { type: String, default: 'inline' },
            collapse: { type: Boolean, default: true },
            loading: Boolean
        },

        data () {
            return {
                modes: [
                    { value: 'inline', label: 'Inline' },
                    { value: 'split', label: 'Side by side' },
                    { value: 'clean', label: 'Clean' }
                ]
            }
        },

        computed: {
            newestFirst () {
                return this.versions.slice().reverse()
            },

            single () {
                return this.versions.length == 1
            },

            // With only one version there is nothing to compare, so show its full text
            effectiveMode () {
                return this.single ? 'clean' : this.mode
            },

            fromVersion () {
                return this.versions.find(version => version.number == this.from)
            },

            toVersion () {
                return this.versions.find(version => version.number == this.to)
            },

            fields () {
                return diffStates(this.fromVersion.state, this.toVersion.state)
            },

            changedFields () {
                return this.fields.filter(field => field.changes).length
            },

            formattingFields () {
                return this.fields.filter(field => field.formatting).length
            },

            totals () {
                return this.fields.reduce((totals, field) => ({
                    added: totals.added + field.added,
                    removed: totals.removed + field.removed
                }), { added: 0, removed: 0 })
            }
        },

        methods: {
            optionLabel (version) {
                return `v${version.number} · ${version.current ? 'Current' : moment(version.at).fromNow()}`
            }
        }
    }
</script>
